import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import numeral from "numeral";

import { AgGridReact } from "@ag-grid-community/react";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ModuleRegistry } from "@ag-grid-community/core";

import { ColumnsToolPanelModule } from "@ag-grid-enterprise/column-tool-panel";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import { ExcelExportModule } from "@ag-grid-enterprise/excel-export";

import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  RowGroupingModule,
  ExcelExportModule,
]);

const formatCurrency = (value) => {
  if (!value) return "0";
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  return numeral(numValue).format("0,0.00");
};

const AnalysisTable = ({ gridRef, onGridReady }) => {
  const { projects, rmkActiveApplication } = useSelector((state) => ({
    projects: state.analysisTableReducer.projectApplications,
    rmkActiveApplication: state.rmkReducer.rmkActiveApplication,
  }));

  // Transform the raw data with project numbering and sums
  const { rowData, grandTotals } = useMemo(() => {
    const formattedData = [];
    let projectCounter = 0;

    // Initialize grand totals with yearly breakdown
    const grandTotals = {
      agencyCost: 0,
      agencyCeiling: 0,
      upenCost: 0,
      upenCeiling: 0,
      jppnCost: 0,
      jppnCeiling: 0,
      cabinetCost: 0,
      cabinetCeiling: 0,
      yearly: {
        agency: [0, 0, 0, 0, 0],
        upen: [0, 0, 0, 0, 0],
        jppn: [0, 0, 0, 0, 0],
        cabinet: [0, 0, 0, 0, 0],
      },
    };

    projects.forEach((project) => {
      const {
        financial_details = [],
        rmk,
        ministry,
        executing_agency,
        branch,
        program,
        main_project,
        name,
        code,
        project_category,
        project_type,
      } = project;
      projectCounter++;

      // Process certification stages
      const processStages = (status, stages) => {
        return financial_details
          .flatMap((fd) =>
            (fd.financial_detail_stages || [])
              .filter(
                (stage) => stage.status === status && stage.stages === stages,
              )
              .map((stage) => ({
                ...stage,
                scope_name: fd.cost_description || stage.scope_name,
                // Add yearly amounts from ceiling_flow_details
                yearly: getYearlyAmounts(
                  fd.ceiling_flow_details,
                  stages,
                  status,
                ),
              })),
          )
          .filter(Boolean);
      };

      // Helper function to extract yearly amounts
      const getYearlyAmounts = (ceilingFlowDetails, stages, status) => {
        const yearly = [0, 0, 0, 0, 0];
        if (!ceilingFlowDetails) return yearly;

        ceilingFlowDetails.forEach((cfd) => {
          if (!cfd.ceiling_flow_detail_stages) return;

          const yearIndex = cfd.year - rmkActiveApplication?.first_year;
          if (yearIndex < 0 || yearIndex > 4) return;

          cfd.ceiling_flow_detail_stages.forEach((stage) => {
            if (stage.stages === stages && stage.status === status) {
              yearly[yearIndex] += parseFloat(stage.amount) || 0;
            }
          });
        });

        return yearly;
      };

      const processApprovedBudgets = () => {
        return financial_details

          .map((fd) => ({
            ...fd,
            // Add yearly amounts from ceiling_flow_details
            scope_name: fd.cost_description || "-",
            yearly: approvedCeilingByYears(fd.ceiling_flow_details),
          }))
          .filter(Boolean);
      };

      const approvedCeilingByYears = (ceilingFlowDetails) => {
        const yearly = [0, 0, 0, 0, 0];
        if (!ceilingFlowDetails) return yearly;

        ceilingFlowDetails.forEach((cfd) => {
          const yearIndex = cfd.year - rmkActiveApplication?.first_year;
          if (yearIndex < 0 || yearIndex > 4) return;

          yearly[yearIndex] += parseFloat(cfd.amount) || 0;
        });

        return yearly;
      };

      const budgetRequest = processStages("2", "4");
      const certifiedByUpen = processStages("3", "5");
      const certifiedByJppn = processStages("3", "6");
      // const cabinetApproved = processStages("4", "6");
      const approvedBudgets = processApprovedBudgets();

      // Calculate project sums including yearly breakdowns
      const sumAgencyCost = budgetRequest.reduce((sum, item) => {
        const value = parseFloat(item.cost_required) || 0;
        grandTotals.agencyCost += value;
        return sum + value;
      }, 0);

      const sumAgencyCeiling = budgetRequest.reduce((sum, item) => {
        const value = parseFloat(item.ceiling) || 0;
        grandTotals.agencyCeiling += value;
        return sum + value;
      }, 0);

      const sumUpenCost = certifiedByUpen.reduce((sum, item) => {
        const value = parseFloat(item.cost_required) || 0;
        grandTotals.upenCost += value;
        return sum + value;
      }, 0);

      const sumUpenCeiling = certifiedByUpen.reduce((sum, item) => {
        const value = parseFloat(item.ceiling) || 0;
        grandTotals.upenCeiling += value;
        return sum + value;
      }, 0);

      const sumJppnCost = certifiedByJppn.reduce((sum, item) => {
        const value = parseFloat(item.cost_required) || 0;
        grandTotals.jppnCost += value;
        return sum + value;
      }, 0);

      const sumJppnCeiling = certifiedByJppn.reduce((sum, item) => {
        const value = parseFloat(item.ceiling) || 0;
        grandTotals.jppnCeiling += value;
        return sum + value;
      }, 0);

      const sumCabinetCost = approvedBudgets.reduce((sum, item) => {
        const value = parseFloat(item.cost_required) || 0;
        grandTotals.cabinetCost += value;
        return sum + value;
      }, 0);

      const sumCabinetCeiling = approvedBudgets.reduce((sum, item) => {
        const value = parseFloat(item.ceiling) || 0;
        grandTotals.cabinetCeiling += value;
        return sum + value;
      }, 0);

      // Calculate yearly totals for this project
      const projectYearly = {
        agency: [0, 0, 0, 0, 0],
        upen: [0, 0, 0, 0, 0],
        jppn: [0, 0, 0, 0, 0],
        cabinet: [0, 0, 0, 0, 0],
      };

      budgetRequest.forEach((item) => {
        item.yearly?.forEach((amount, index) => {
          projectYearly.agency[index] += amount;
          grandTotals.yearly.agency[index] += amount;
        });
      });

      certifiedByUpen.forEach((item) => {
        item.yearly?.forEach((amount, index) => {
          projectYearly.upen[index] += amount;
          grandTotals.yearly.upen[index] += amount;
        });
      });

      certifiedByJppn.forEach((item) => {
        item.yearly?.forEach((amount, index) => {
          projectYearly.jppn[index] += amount;
          grandTotals.yearly.jppn[index] += amount;
        });
      });

      approvedBudgets.forEach((item) => {
        item.yearly?.forEach((amount, index) => {
          projectYearly.cabinet[index] += amount;
          grandTotals.yearly.cabinet[index] += amount;
        });
      });

      // Add project rows
      const maxRows = Math.max(
        budgetRequest.length,
        certifiedByUpen.length,
        certifiedByJppn.length,
        approvedBudgets.length,
        1,
      );

      for (let i = 0; i < maxRows; i++) {
        const requested = budgetRequest[i] || {};
        const upen = certifiedByUpen[i] || {};
        const jppn = certifiedByJppn[i] || {};
        const cabinet = approvedBudgets[i] || {};

        formattedData.push({
          projectNumber: i === 0 ? projectCounter : "",
          rmk: i === 0 ? rmk?.short_form || "" : "",
          // ministry: i === 0 ? ministry?.value || "" : "",
          ministry: ministry?.value || "",
          // executing_agency: i === 0 ? executing_agency?.value || "" : "",
          executing_agency: executing_agency?.value || "",
          branch: i === 0 ? branch?.value || "" : "",
          program: i === 0 ? program?.value || "" : "",
          main_project: i === 0 ? main_project?.value || "" : "",
          name: i === 0 ? name || "" : "",
          project_code:
            i === 0
              ? `D${ministry?.code}-${executing_agency?.code}-000000-${
                  program?.code || "XXXXXX"
                }-${main_project?.code || "XXXX"}${code}-00000` || ""
              : "",
          project_category:
            i === 0
              ? `${project_category?.code} - ${project_category?.value}`
              : "",
          project_type: i === 0 ? project_type?.name : "",
          // Agency data
          agency_scope_name: requested.scope_name || "-",
          agency_cost_required: formatCurrency(requested.cost_required || "0"),
          agency_ceiling: formatCurrency(requested.ceiling || "0"),
          agency_first_year: formatCurrency(requested.yearly?.[0] || "0"),
          agency_second_year: formatCurrency(requested.yearly?.[1] || "0"),
          agency_third_year: formatCurrency(requested.yearly?.[2] || "0"),
          agency_fourth_year: formatCurrency(requested.yearly?.[3] || "0"),
          agency_fifth_year: formatCurrency(requested.yearly?.[4] || "0"),

          // UPEN data
          upen_scope_name: upen.scope_name || "-",
          upen_cost_required: formatCurrency(upen.cost_required || "0"),
          upen_ceiling: formatCurrency(upen.ceiling || "0"),
          upen_first_year: formatCurrency(upen.yearly?.[0] || "0"),
          upen_second_year: formatCurrency(upen.yearly?.[1] || "0"),
          upen_third_year: formatCurrency(upen.yearly?.[2] || "0"),
          upen_fourth_year: formatCurrency(upen.yearly?.[3] || "0"),
          upen_fifth_year: formatCurrency(upen.yearly?.[4] || "0"),

          // JPPN data
          jppn_scope_name: jppn.scope_name || "-",
          jppn_cost_required: formatCurrency(jppn.cost_required || "0"),
          jppn_ceiling: formatCurrency(jppn.ceiling || "0"),
          jppn_first_year: formatCurrency(jppn.yearly?.[0] || "0"),
          jppn_second_year: formatCurrency(jppn.yearly?.[1] || "0"),
          jppn_third_year: formatCurrency(jppn.yearly?.[2] || "0"),
          jppn_fourth_year: formatCurrency(jppn.yearly?.[3] || "0"),
          jppn_fifth_year: formatCurrency(jppn.yearly?.[4] || "0"),

          // Cabinet data
          cabinet_scope_name: cabinet.scope_name || "-",
          cabinet_cost_required: formatCurrency(cabinet.cost_required || "0"),
          cabinet_ceiling: formatCurrency(cabinet.ceiling || "0"),
          cabinet_first_year: formatCurrency(cabinet.yearly?.[0] || "0"),
          cabinet_second_year: formatCurrency(cabinet.yearly?.[1] || "0"),
          cabinet_third_year: formatCurrency(cabinet.yearly?.[2] || "0"),
          cabinet_fourth_year: formatCurrency(cabinet.yearly?.[3] || "0"),
          cabinet_fifth_year: formatCurrency(cabinet.yearly?.[4] || "0"),

          showProjectInfo: i === 0,
          rowType: "detail",
        });
      }

      // // Add project summary row with yearly totals
      // formattedData.push({
      //   projectNumber: "",
      //   rmk: "",
      //   ministry: "",
      //   executing_agency: "",
      //   branch: "",
      //   program: "",
      //   main_project: "",
      //   name: `${name} - JUMLAH KESELURUHAN`,
      //   project_code: "",
      //   project_category: "",
      //   project_type: "",

      //   // Agency totals
      //   agency_scope_name: "",
      //   agency_cost_required: formatCurrency(sumAgencyCost),
      //   agency_ceiling: formatCurrency(sumAgencyCeiling),
      //   agency_first_year: formatCurrency(projectYearly.agency[0]),
      //   agency_second_year: formatCurrency(projectYearly.agency[1]),
      //   agency_third_year: formatCurrency(projectYearly.agency[2]),
      //   agency_fourth_year: formatCurrency(projectYearly.agency[3]),
      //   agency_fifth_year: formatCurrency(projectYearly.agency[4]),

      //   // UPEN totals
      //   upen_scope_name: "",
      //   upen_cost_required: formatCurrency(sumUpenCost),
      //   upen_ceiling: formatCurrency(sumUpenCeiling),
      //   upen_year_first_year: formatCurrency(projectYearly.upen[0]),
      //   upen_year_second_year: formatCurrency(projectYearly.upen[1]),
      //   upen_year_third_year: formatCurrency(projectYearly.upen[2]),
      //   upen_year_fourth_year: formatCurrency(projectYearly.upen[3]),
      //   upen_year_fifth_year: formatCurrency(projectYearly.upen[4]),

      //   // JPPN totals
      //   jppn_scope_name: "",
      //   jppn_cost_required: formatCurrency(sumJppnCost),
      //   jppn_ceiling: formatCurrency(sumJppnCeiling),
      //   jppn_year_first_year: formatCurrency(projectYearly.jppn[0]),
      //   jppn_year_second_year: formatCurrency(projectYearly.jppn[1]),
      //   jppn_year_third_year: formatCurrency(projectYearly.jppn[2]),
      //   jppn_year_fourth_year: formatCurrency(projectYearly.jppn[3]),
      //   jppn_year_fifth_year: formatCurrency(projectYearly.jppn[4]),

      //   // Cabinet totals
      //   cabinet_scope_name: "",
      //   cabinet_cost_required: formatCurrency(sumCabinetCost),
      //   cabinet_ceiling: formatCurrency(sumCabinetCeiling),
      //   cabinet_year_first_year: formatCurrency(projectYearly.cabinet[0]),
      //   cabinet_year_second_year: formatCurrency(projectYearly.cabinet[1]),
      //   cabinet_year_third_year: formatCurrency(projectYearly.cabinet[2]),
      //   cabinet_year_fourth_year: formatCurrency(projectYearly.cabinet[3]),
      //   cabinet_year_fifth_year: formatCurrency(projectYearly.cabinet[4]),

      //   showProjectInfo: false,
      //   rowType: "project-total",
      // });
    });

    return { rowData: formattedData, grandTotals };
  }, [projects]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "#",
        width: 60,
        suppressMovable: true,
        valueGetter: (params) => {
          if (params.node.group) {
            // For group rows, show nothing or group-specific numbering if desired
            return ""; // or return params.node.key if you want to show group key
          }

          if (!params.data.showProjectInfo) return "";

          // Get all visible project header rows in the same group
          const groupRows = [];
          let currentNode = params.node;

          // Find the parent group node if exists
          const parentGroup = params.node.parent;

          if (parentGroup) {
            // Get all visible project headers in this group
            parentGroup.childrenAfterGroup.forEach((node) => {
              if (node.data?.showProjectInfo) {
                groupRows.push(node);
              }
            });

            // Return position within the group (1-based index)
            return groupRows.indexOf(params.node) + 1;
          } else {
            // For non-grouped rows, fall back to global numbering
            const allVisibleProjects = [];
            params.api.forEachNodeAfterFilterAndSort((node) => {
              if (node.data?.showProjectInfo && !node.parent) {
                allVisibleProjects.push(node);
              }
            });
            return allVisibleProjects.indexOf(params.node) + 1;
          }
        },
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
      // {
      //   headerName: "#",
      //   field: "projectNumber",
      //   width: 60,
      //   suppressMovable: true,
      //   valueGetter: (params) => params.data?.projectNumber || "", // Add null check
      //   cellStyle: (params) => ({
      //     display: "flex",
      //     alignItems: "center",
      //     justifyContent: "center",
      //   }),
      // },
      {
        headerName: "RMK",
        field: "rmk",
        width: 100,
        suppressMovable: true,
        cellStyle: (params) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }),
      },
      {
        headerName: "KEMENTERIAN",
        field: "ministry",
        width: 300,
        autoHeight: true,
        enableRowGroup: true,
        cellStyle: (params) => ({
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.5",
          display: "flex",
          alignItems: "center",
        }),
      },
      {
        headerName: "JABATAN",
        field: "executing_agency",
        width: 300,
        autoHeight: true,
        enableRowGroup: true,
        cellStyle: (params) => ({
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.5",
          display: "flex",
          alignItems: "center",
        }),
      },
      {
        headerName: "CAWANGAN",
        field: "branch",
        width: 300,
        autoHeight: true,
        enableRowGroup: true,
        cellStyle: (params) => ({
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.5",
          display: "flex",
          alignItems: "center",
        }),
      },
      {
        headerName: "PROGAM",
        field: "program",
        width: 300,
        autoHeight: true,
        cellStyle: (params) => ({
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.5",
          display: "flex",
          alignItems: "center",
        }),
      },
      {
        headerName: "PROJEK UTAMA",
        field: "main_project",
        width: 300,
        autoHeight: true,
        cellStyle: (params) => ({
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.5",
          display: "flex",
          alignItems: "center",
        }),
      },
      {
        headerName: "NAMA PROJEK",
        field: "name",
        width: 300,
        autoHeight: true,
        cellStyle: (params) => ({
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.5",
          display: "flex",
          alignItems: "center",
        }),
      },
      {
        headerName: "KOD PENUH PROJEK",
        field: "project_code",
        width: 340,
        autoHeight: true,
      },
      {
        headerName: "KATEGORI PROJEK",
        field: "project_category",
        autoHeight: true,
      },
      {
        headerName: "JENIS PROJEK",
        field: "project_type",
        autoHeight: true,
      },
      {
        headerName: "PERMOHONAN AGENSI",
        headerClass: "ag-center-header",
        children: [
          {
            headerName: "NAMA SKOP",
            field: "agency_scope_name",
            sortable: false,
            width: 320,
            autoHeight: true,
            cellStyle: (params) => ({
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "1.5",
            }),
          },
          {
            headerName: "KOS KESELURUHAN",
            field: "agency_cost_required",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: "SILING KESELURUHAN",
            field: "agency_ceiling",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.first_year}`,
            suppressMovable: true,
            field: "agency_first_year",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.second_year}`,
            suppressMovable: true,
            field: "agency_second_year",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.third_year}`,
            suppressMovable: true,
            field: "agency_third_year",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.fourth_year}`,
            suppressMovable: true,
            field: "agency_fourth_year",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.fifth_year}`,
            suppressMovable: true,
            field: "agency_fifth_year",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
        ],
      },
      {
        headerName: "DIPERAKU UNIT PERANCANG EKONOMI NEGERI (UPEN)",
        children: [
          {
            headerName: "NAMA SKOP",
            field: "upen_scope_name",
            sortable: false,
            width: 320,
            autoHeight: true,
            cellStyle: (params) => ({
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "1.5",
            }),
          },
          {
            headerName: "KOS KESELURUHAN",
            field: "upen_cost_required",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: "SILING KESELURUHAN",
            field: "upen_ceiling",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.first_year}`,
            field: "upen_first_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.second_year}`,
            field: "upen_second_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.third_year}`,
            field: "upen_third_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.fourth_year}`,
            field: "upen_fourth_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.fifth_year}`,
            field: "upen_fifth_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
        ],
      },
      {
        headerName: "DIPERAKU JAWATANKUASA PERANCANG PEMBANGUNAN NEGERI (JPPN)",
        children: [
          {
            headerName: "NAMA SKOP",
            field: "jppn_scope_name",
            sortable: false,
            width: 320,
            autoHeight: true,
            cellStyle: (params) => ({
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "1.5",
            }),
          },
          {
            headerName: "KOS KESELURUHAN",
            field: "jppn_cost_required",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: "SILING KESELURUHAN",
            field: "jppn_ceiling",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.first_year}`,
            field: "jppn_first_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.second_year}`,
            field: "jppn_second_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.third_year}`,
            field: "jppn_third_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.fourth_year}`,
            field: "jppn_fourth_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.fifth_year}`,
            field: "jppn_fifth_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
        ],
      },
      {
        headerName: "KELULUSAN KABINET",
        children: [
          {
            headerName: "NAMA SKOP",
            field: "cabinet_scope_name",
            sortable: false,
            width: 320,
            autoHeight: true,
            cellStyle: (params) => ({
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "1.5",
            }),
          },
          {
            headerName: "KOS KESELURUHAN",
            field: "cabinet_cost_required",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: "SILING KESELURUHAN",
            field: "cabinet_ceiling",
            width: 200,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.first_year}`,
            field: "cabinet_first_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.second_year}`,
            field: "cabinet_second_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.third_year}`,
            field: "cabinet_third_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.fourth_year}`,
            field: "cabinet_fourth_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
          {
            headerName: `TAHUN ${rmkActiveApplication?.fifth_year}`,
            field: "cabinet_fifth_year",
            width: 200,
            suppressMovable: true,
            cellClass: "ag-right-aligned-cell",
            aggFunc: "jumlah",
          },
        ],
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      cellStyle: {
        padding: "8px",
        borderRight: "1px solid #eee",
      },
    }),
    [],
  );

  const autoGroupColumnDef = {
    headerName: "Group",
    cellRendererParams: {
      innerRenderer: (params) => {
        const isFooter = params.node.footer === true;

        const label = isFooter
          ? "Jumlah Keseluruhan"
          : params.value || params.node?.key || "Group";

        const uniqueCount = calculateUniqueProjects(
          params.node.allLeafChildren,
        );

        if (isFooter) {
          return `${label}`;
        } else {
          return `${label} (${uniqueCount} Projek)`;
        }
      },
      suppressCount: true, // We’ll replace it with custom count
    },
  };

  const aggFuncs = useMemo(() => {
    return {
      // this overrides the grids built in sum function
      jumlah: ({ values }) => {
        const total = values
          .map((val) => numeral(val).value())
          .reduce((acc, val) => (acc += parseFloat(val)), 0);
        return numeral(total).format("0,0.00");
      },
      // this adds another function called 'abc'
      // 123: oneTwoThreeFunc,
      // and again xyz
      // xyz: xyzFunc,
    };
  }, []);

  const calculateUniqueProjects = (nodes) => {
    const projectCodes = new Set();

    nodes.forEach((node) => {
      const code = node?.data?.name;
      if (code) {
        projectCodes.add(code);
      }
    });

    return projectCodes.size;
  };

  // Download Execel
  const exportToExcel = useCallback(() => {
    const pad = (n) => n.toString().padStart(2, "0");
    const now = new Date();
    const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
      now.getDate(),
    )}`;
    const time = `${pad(now.getHours())}${pad(now.getMinutes())}`;

    const params = {
      fileName: `Jadual_Analisis_Dalaman_${date}_${time}.xlsx`,
      sheetName: "jad",
      processRowGroupCallback: (params) => {
        return params.node.key; // Customize group row text, remove arrow
      },
    };
    // gridRef.current.api.exportDataAsExcel(params);
    gridRef.current.exportDataAsExcel(params);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <button
          onClick={exportToExcel}
          style={{
            padding: "8px 16px",
            backgroundColor: "#4CAF50",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>Eksport ke Excel</span>
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'></path>
            <polyline points='7 10 12 15 17 10'></polyline>
            <line x1='12' y1='15' x2='12' y2='3'></line>
          </svg>
        </button>
      </div>
      <div
        className='ag-theme-alpine'
        style={{
          height: "85vh",
          width: "100%",
          fontSize: "14px",
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          suppressRowTransform={true}
          headerHeight={40}
          modules={[ClientSideRowModelModule, ExcelExportModule]}
          suppressCellFocus={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          suppressContextMenu={true}
          sideBar={{
            toolPanels: ["columns"],
            defaultToolPanel: undefined,
          }}
          isExternalFilterPresent={() => true}
          doesExternalFilterPass={(node) => {
            return !node.data.isTotalRow;
          }}
          pivotMode={false}
          aggFuncs={aggFuncs}
          suppressAggFuncInHeader={true}
          suppressPivotMode={true}
          groupDisplayType='singleColumn'
          autoGroupColumnDef={autoGroupColumnDef}
          groupIncludeTotalFooter={true}
          overlayLoadingTemplate={
            '<span class="ag-overlay-loading-center">Loading...</span>'
          }
        />
      </div>
    </div>
  );
};

export default AnalysisTable;
